import monitor from "@/utils/monitoring";
import { apiFetch } from "@/services/api/restApi";

export type IKycApi = {
  getApplicantAndToken: () => Promise<
    | { error: Error; data: undefined }
    | { error: undefined; data: { applicantId: string; token: string } }
  >;
  createChecks: (_: {
    applicantId: string;
    variant: string;
  }) => Promise<
    { error: Error; data: undefined } | { error: undefined; data: { checkId: string } }
  >;
  getChecks: (_: {
    checkId: string;
  }) => Promise<
    | { error: Error; data: undefined }
    | { error: undefined; data: { status: string; result: string; reportIds: readonly string[] } }
  >;
  getReports: (_: {
    reportIds: readonly string[];
  }) => Promise<
    ReadonlyArray<
      { error: Error; data: undefined } | { error: undefined; data: { subResult: string } }
    >
  >;
  downloadCheck: (_: {
    checkId: string;
  }) => Promise<{ error: Error; data: undefined } | { error: undefined; data: any }>;
};

export const getApplicantAndToken = async (args: { caseId: string; signature: string | null }) => {
  const { caseId, signature } = args;

  let result;
  try {
    result = await apiFetch({
      path: `/notifier/onfido/get_applicant_and_token`,
      search: !signature ? undefined : { caseId, sig: signature },
    });
  } catch (error) {
    monitor.logError({
      event: "GET /notifier/onfido/get_applicant_and_token",
      args,
      error,
    });
    throw error;
  }

  return result;
};

export const createChecks = async (args: {
  caseId: string;
  signature: string | null;
  variant: string;
}) => {
  const { caseId, signature, variant } = args;

  let result;
  try {
    result = await apiFetch({
      path: `/notifier/onfido/checks`,
      search: !signature ? undefined : { caseId, sig: signature },
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: variant ? { variant } : {},
    });
  } catch (error) {
    monitor.logError({
      event: "POST /notifier/onfido/checks",
      args,
      error,
    });
    throw error;
  }

  return result;
};

export const getChecks = async (args: {
  caseId: string;
  signature: string | null;
  checkId: string;
}) => {
  const { caseId, signature, checkId } = args;

  let result;
  try {
    result = await apiFetch({
      path: `/notifier/onfido/checks/${checkId}`,
      search: !signature ? undefined : { caseId, sig: signature },
    });
  } catch (error) {
    monitor.logError({
      event: "GET /notifier/onfido/checks/{checkId}",
      args,
      error,
    });
    throw error;
  }

  return result;
};

export const downloadCheck = async (args: {
  caseId: string;
  signature: string | null;
  checkId: string;
}) => {
  const { caseId, signature, checkId } = args;

  let result;
  try {
    result = await apiFetch({
      path: `/notifier/onfido/checks/${checkId}/download`,
      search: !signature ? undefined : { caseId, sig: signature },
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: {},
    });
  } catch (error) {
    monitor.logError({
      event: "POST /notifier/onfido/checks/{checkId}/download",
      args,
      error,
    });
    throw error;
  }

  return result;
};

export const getReports = (args: {
  caseId: string;
  signature: string | null;
  reportIds: readonly string[];
}) => {
  const { caseId, signature, reportIds } = args;

  return Promise.all(
    reportIds.map((reportId) => {
      return getReport({ caseId, signature, reportId });
    })
  );
};

const getReport = async (args: { caseId: string; signature: string | null; reportId: string }) => {
  const { caseId, signature, reportId } = args;

  let result;
  try {
    result = await apiFetch({
      path: `/notifier/onfido/reports/${reportId}`,
      search: !signature ? undefined : { caseId, sig: signature },
    });
  } catch (error) {
    monitor.logError({
      event: "GET /notifier/onfido/reports/{reportId}",
      args,
      error,
    });
    throw error;
  }

  return result;
};
